import type { UseFormSetError } from 'react-hook-form';
import type { User } from '@/utils/types/User';
import { type Renter } from '../../utils/types/renter.interface';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box } from '@mui/material';

import { Divider } from '@/components/Divider';
import { checkIfPmi } from '@/utils/commonUtils';
import {
  MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE,
  SELF_ENROLLED_BUILDING_ATTR,
} from '@/utils/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import { CoverageRequirementsAlert } from './content/invite-lease-details.drawer.coverage-requirements-alert';
import CoverageSelection from './content/invite-lease-details.drawer.coverage-selection';
import PmiCoverageSelection from './coverage-selection';
import { LeaseInformation } from './content/invite-lease-details.drawer.lease-information';
import { RenterContact } from './content/invite-lease-details.drawer.renter-contact';
import { InviteLeaseDetailsDrawerScreeningResults } from './content/invite-lease-details.drawer.screening-results';
import { ScreeningResult } from './constants';
import { Button, theme } from '@theguarantors/tg-package-material-ui';
import { useInviteLeaseDetailsDrawerStore } from '@/stores/use-invite-lease-details-drawer.store';
import { calculateLeaseDurationMonths } from './utils/coverage.util';
import { Tooltip } from '@/components/Tooltip';
import {
  useInviteFormTracking,
  useSmbCoverageOptions,
  useTrackCoveragePricingResolved,
  useInviteLeaseDetailsForm,
} from './hooks';
import type { InviteFlowMilestoneKey } from './types';

interface InviteLeaseDetailsDrawerFormProps {
  onSubmit: (
    values: Renter,
    setError: UseFormSetError<Renter>,
    submitAttemptNumber?: number,
  ) => void;
  errorMsg: string;
  user?: User;
  newCoveragesEnabled?: boolean;
  customCoverageDisabled?: boolean;
  isSmbInvite?: boolean;
  onMilestoneReached?: (key: InviteFlowMilestoneKey) => void;
}

export const InviteLeaseDetailsDrawerFormDivider = () => (
  <Divider
    sx={{
      borderColor: `${theme.palette.neutralGrey[200]} !important`,
      my: 3,
      [theme.breakpoints.down('md')]: {
        borderColor: `transparent !important`,
        // height: '24px !important',
        color: 'transparent !important',
        my: 1.5,
      },
    }}
  />
);

export const InviteLeaseDetailsDrawerForm = ({
  onSubmit,
  errorMsg,
  user,
  newCoveragesEnabled,
  customCoverageDisabled = false,
  isSmbInvite = true,
  onMilestoneReached,
}: InviteLeaseDetailsDrawerFormProps) => {
  const [isSelfEnrolled, setIsSelfEnrolled] = useState<boolean>(false);
  const submitAttemptRef = useRef(0);
  const { trackFlowStarted, trackValidationErrors } = useInviteFormTracking({
    onFlowStarted: () => onMilestoneReached?.('hadFlowStarted'),
  });

  const selfEnrolledSDRLimit = useMemo(() => {
    if (!user) return;
    if (checkIfPmi(user)) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.PMI;
    }
    if (isSelfEnrolled) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.SELF_ENROLLED;
    }
  }, [user, isSelfEnrolled]);

  const { handleSubmit, watch, control, trigger, setError, resetField, setValue, formState } =
    useInviteLeaseDetailsForm({
      selfEnrolledSDRLimit,
      customCoverageDisabled,
      useNewCoveragesOnLandlordInvite: newCoveragesEnabled === true,
    });

  const { isSubmitting, isValid, errors, isDirty, dirtyFields } = formState;

  // Update store's dirty state ref during render (no useEffect needed)
  // Accessing isDirty and dirtyFields here subscribes to react-hook-form updates
  const { dirtyStateRef } = useInviteLeaseDetailsDrawerStore();
  dirtyStateRef.current = { isDirty, dirtyFields };

  const customCoverage = watch('customCoverage');
  const address = watch('address');

  useEffect(() => {
    // Skip when customCoverage is undefined (happens during re-renders with shouldUnregister: true)
    if (customCoverage === undefined) {
      return;
    }

    if (customCoverage) {
      resetField('approvalStatus');
      setValue('rentCoverage', RentCoverageTypes.Customize);
      setValue('securityDepositCoverage', SecurityDepositTypes.CustomizeOrRemove);
    } else {
      setValue('rentCoverage', RentCoverageTypes.CompanyDefault);
      setValue('securityDepositCoverage', SecurityDepositTypes.CompanyDefault);
    }
  }, [customCoverage, resetField, setValue]);

  useEffect(() => {
    setIsSelfEnrolled(address?.source === SELF_ENROLLED_BUILDING_ATTR);
  }, [address]);

  const leaseStart = watch('leaseStart');
  const leaseEnd = watch('leaseEnd');
  const leaseDurationMonths = calculateLeaseDurationMonths(leaseStart, leaseEnd);
  const monthsOfCoverage = watch('monthsOfCoverage');
  const grossMonthlyRent = watch('grossMonthlyRent');
  const freeRent = watch('freeRent');
  const prepaidRent = watch('prepaidRent');

  const {
    coverageOptions,
    selectedCoverageOption,
    isUsingDynamicPricing,
    isLoading: isCoverageOptionsLoading,
    isError: isCoverageOptionsError,
    error: coverageOptionsError,
  } = useSmbCoverageOptions({
    propertyId: address?.property_id,
    leaseStart,
    leaseEnd,
    grossMonthlyRent,
    freeRent,
    prepaidRent,
    leaseDurationMonths,
    selectedValue: monthsOfCoverage,
    enabled: newCoveragesEnabled && isSmbInvite,
  });

  useTrackCoveragePricingResolved({
    propertyId: address?.property_id,
    enabled: isSmbInvite && isUsingDynamicPricing,
    isLoading: isCoverageOptionsLoading,
    isError: isCoverageOptionsError,
    error: coverageOptionsError,
    hasCustomScreeningStatusLabels: false,
    pricingOptionCount: coverageOptions?.length ?? 0,
    pricingData: coverageOptions,
  });

  useEffect(() => {
    if (!newCoveragesEnabled || !isSmbInvite) return;

    if (!monthsOfCoverage) {
      setValue('approvalStatus', '', { shouldValidate: false });
      return;
    }

    if (!selectedCoverageOption || selectedCoverageOption.disabled) {
      setValue('approvalStatus', '', { shouldValidate: false });
      setValue('monthsOfCoverage', undefined);
      return;
    }

    if (customCoverage) {
      setValue('approvalStatus', ScreeningResult.Denied, { shouldValidate: false });
      return;
    }

    if (watch('approvalStatus') !== selectedCoverageOption.screeningResult) {
      setValue('approvalStatus', selectedCoverageOption.screeningResult, { shouldValidate: false });
    }
  }, [
    customCoverage,
    isSmbInvite,
    monthsOfCoverage,
    newCoveragesEnabled,
    selectedCoverageOption,
    setValue,
    watch,
  ]);

  const handleFormSubmit = handleSubmit(
    (values) => {
      submitAttemptRef.current += 1;
      onMilestoneReached?.('hadSubmitAttempt');

      const payload = newCoveragesEnabled
        ? { ...values, selectedSmbPricing: selectedCoverageOption?.pricing }
        : values;

      return onSubmit(payload, setError, submitAttemptRef.current);
    },
    (fieldErrors) => {
      submitAttemptRef.current += 1;
      onMilestoneReached?.('hadSubmitAttempt');
      trackValidationErrors(fieldErrors, 'client');
    },
  );

  return (
    <form
      id={'renter-invite-form'}
      style={{ margin: 0 }}
      onSubmit={handleFormSubmit}
      onFocusCapture={() => trackFlowStarted('field_focus')}
      onClickCapture={() => trackFlowStarted('click')}
      onKeyDownCapture={() => trackFlowStarted('typing')}
      noValidate
    >
      {customCoverageDisabled && <CoverageRequirementsAlert />}
      <LeaseInformation
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        trigger={trigger}
        onMilestoneReached={onMilestoneReached}
      />
      <InviteLeaseDetailsDrawerFormDivider />
      <RenterContact control={control} errors={errors} watch={watch} trigger={trigger} />
      <InviteLeaseDetailsDrawerFormDivider />
      <InviteLeaseDetailsDrawerScreeningResults
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        isSmbInvite={isSmbInvite}
        newCoveragesEnabled={newCoveragesEnabled}
        onMilestoneReached={onMilestoneReached}
        coverageLengthOptions={coverageOptions}
        showCoverageSkeleton={Boolean(
          isSmbInvite && newCoveragesEnabled && isCoverageOptionsLoading,
        )}
      />
      {((isSmbInvite && !!customCoverage) || (!isSmbInvite && !customCoverageDisabled)) && (
        <>
          <InviteLeaseDetailsDrawerFormDivider />
          {user && checkIfPmi(user) ? (
            <PmiCoverageSelection
              control={control}
              errors={errors}
              selfEnrolledSDRLimit={selfEnrolledSDRLimit}
            />
          ) : (
            <CoverageSelection
              control={control}
              errors={errors}
              watch={watch}
              useDrawerStyles={true}
              selfEnrolledSDRLimit={selfEnrolledSDRLimit}
            />
          )}
        </>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ marginY: 2 }}>
          {errorMsg}
        </Alert>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: -1,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${theme.palette.neutralGrey[200]}`,
          pt: 2,
          pb: 2,
          mt: 2,
          zIndex: 100,
          mx: -4,
          px: 4,
        }}
      >
        <Tooltip title={!isValid ? 'Please complete the required fields to proceed' : ''}>
          <Box>
            <Button
              variant="contained"
              type="submit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              id={'RenterInviteSubmitButton'}
              style={!isValid || isSubmitting ? { pointerEvents: 'none' } : {}}
              sx={{ width: '100%', height: '55px' }}
            >
              Send Invite
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </form>
  );
};
